import React, { useState } from 'react';
import { BarChart3Icon, CheckCircleIcon, ClipboardListIcon, StarIcon, TrendingUpIcon, UserIcon, ShoppingBagIcon, DollarSignIcon } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
const assessmentCategories = [{
  id: 'sales',
  name: 'Sales Performance',
  icon: <DollarSignIcon className="h-5 w-5 text-blue-600" data-id="element-1489" />,
  description: 'Evaluate your overall sales volume and growth'
}, {
  id: 'customer',
  name: 'Customer Satisfaction',
  icon: <UserIcon className="h-5 w-5 text-blue-600" data-id="element-1490" />,
  description: 'Assess customer feedback and satisfaction levels'
}, {
  id: 'operations',
  name: 'Operational Efficiency',
  icon: <ClipboardListIcon className="h-5 w-5 text-blue-600" data-id="element-1491" />,
  description: 'Evaluate your business processes and efficiency'
}, {
  id: 'growth',
  name: 'Business Growth',
  icon: <TrendingUpIcon className="h-5 w-5 text-blue-600" data-id="element-1492" />,
  description: 'Assess your business growth and expansion'
}, {
  id: 'inventory',
  name: 'Inventory Management',
  icon: <ShoppingBagIcon className="h-5 w-5 text-blue-600" data-id="element-1493" />,
  description: 'Evaluate your inventory control and management'
}];
const recommendations = {
  sales: [{
    score: 1,
    text: 'Consider reviewing your pricing strategy and product offerings.'
  }, {
    score: 2,
    text: 'Implement targeted promotions to boost sales volume.'
  }, {
    score: 3,
    text: 'Your sales are stable. Consider expanding your product range.'
  }, {
    score: 4,
    text: 'Strong sales performance. Focus on maintaining momentum.'
  }, {
    score: 5,
    text: 'Excellent sales performance. Consider scaling operations.'
  }],
  customer: [{
    score: 1,
    text: 'Urgently address customer service issues and gather feedback.'
  }, {
    score: 2,
    text: 'Implement a customer feedback system to identify improvement areas.'
  }, {
    score: 3,
    text: 'Customer satisfaction is adequate. Consider loyalty programs.'
  }, {
    score: 4,
    text: 'Good customer satisfaction. Focus on personalized experiences.'
  }, {
    score: 5,
    text: 'Excellent customer satisfaction. Leverage for referrals.'
  }],
  operations: [{
    score: 1,
    text: 'Review and restructure operational processes to reduce inefficiencies.'
  }, {
    score: 2,
    text: 'Identify bottlenecks in operations and implement improvements.'
  }, {
    score: 3,
    text: 'Operations are running smoothly. Look for optimization opportunities.'
  }, {
    score: 4,
    text: 'Efficient operations. Consider technology upgrades for further improvement.'
  }, {
    score: 5,
    text: 'Highly efficient operations. Share best practices across the organization.'
  }],
  growth: [{
    score: 1,
    text: 'Develop a strategic growth plan focusing on core strengths.'
  }, {
    score: 2,
    text: 'Identify new market opportunities and potential partnerships.'
  }, {
    score: 3,
    text: 'Steady growth. Consider diversification strategies.'
  }, {
    score: 4,
    text: 'Strong growth trajectory. Prepare for scaling challenges.'
  }, {
    score: 5,
    text: 'Exceptional growth. Ensure infrastructure can support expansion.'
  }],
  inventory: [{
    score: 1,
    text: 'Implement basic inventory tracking and management systems.'
  }, {
    score: 2,
    text: 'Review stock levels and optimize ordering processes.'
  }, {
    score: 3,
    text: 'Adequate inventory management. Consider just-in-time approaches.'
  }, {
    score: 4,
    text: 'Good inventory control. Look for ways to reduce carrying costs.'
  }, {
    score: 5,
    text: 'Excellent inventory management. Consider automated replenishment.'
  }]
};
const MerchantAssessmentPage = () => {
  const [scores, setScores] = useState({
    sales: 0,
    customer: 0,
    operations: 0,
    growth: 0,
    inventory: 0
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [assessmentDate, setAssessmentDate] = useState('');
  const handleScoreChange = (category, score) => {
    setScores({
      ...scores,
      [category]: score
    });
  };
  const calculateOverallScore = () => {
    const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
    return Math.round(totalScore / (Object.keys(scores).length * 5) * 100);
  };
  const getRecommendation = category => {
    const score = scores[category];
    if (score === 0) return null;
    return recommendations[category].find(rec => rec.score === score)?.text;
  };
  const handleSubmit = e => {
    e.preventDefault();
    const today = new Date();
    setAssessmentDate(today.toLocaleDateString());
    setIsSubmitted(true);
    window.scrollTo(0, 0);
  };
  const handleReset = () => {
    setScores({
      sales: 0,
      customer: 0,
      operations: 0,
      growth: 0,
      inventory: 0
    });
    setIsSubmitted(false);
  };
  const allCategoriesRated = Object.values(scores).every(score => score > 0);
  return <DashboardLayout data-id="element-1494">
      <div className="mb-6" data-id="element-1495">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1496">
          Merchant Assessment
        </h1>
        <p className="text-gray-600" data-id="element-1497">
          Evaluate your business performance and receive tailored
          recommendations
        </p>
      </div>
      {isSubmitted && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1498">
          <div className="flex items-center mb-4" data-id="element-1499">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" data-id="element-1500" />
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-1501">
              Assessment Complete
            </h2>
          </div>
          <p className="text-gray-600 mb-4" data-id="element-1502">
            Assessment completed on {assessmentDate}
          </p>
          <div className="mb-6" data-id="element-1503">
            <div className="flex justify-between items-center mb-2" data-id="element-1504">
              <span className="text-sm font-medium text-gray-700" data-id="element-1505">
                Overall Performance
              </span>
              <span className="text-sm font-medium text-blue-600" data-id="element-1506">
                {calculateOverallScore()}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1507">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{
            width: `${calculateOverallScore()}%`
          }} data-id="element-1508"></div>
            </div>
          </div>
          <h3 className="font-medium text-gray-900 mb-4" data-id="element-1509">
            Performance Breakdown
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1510">
            {assessmentCategories.map(category => <div key={category.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1511">
                <div className="flex items-center justify-between mb-2" data-id="element-1512">
                  <div className="flex items-center" data-id="element-1513">
                    {category.icon}
                    <span className="ml-2 font-medium" data-id="element-1514">{category.name}</span>
                  </div>
                  <div className="flex" data-id="element-1515">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < scores[category.id] ? 'text-yellow-400' : 'text-gray-300'}`} data-id="element-1516" />)}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2" data-id="element-1517">
                  {getRecommendation(category.id)}
                </p>
              </div>)}
          </div>
          <div className="flex justify-center" data-id="element-1518">
            <button onClick={handleReset} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1519">
              Start New Assessment
            </button>
          </div>
        </div>}
      {!isSubmitted && <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1520">
          <h2 className="text-lg font-semibold mb-6" data-id="element-1521">
            Rate Your Business Performance
          </h2>
          <form onSubmit={handleSubmit} data-id="element-1522">
            <div className="space-y-6" data-id="element-1523">
              {assessmentCategories.map(category => <div key={category.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1524">
                  <div className="flex items-center mb-2" data-id="element-1525">
                    {category.icon}
                    <h3 className="ml-2 font-medium" data-id="element-1526">{category.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1527">
                    {category.description}
                  </p>
                  <div className="flex items-center" data-id="element-1528">
                    <span className="text-sm text-gray-500 mr-4" data-id="element-1529">Poor</span>
                    <div className="flex space-x-2" data-id="element-1530">
                      {[1, 2, 3, 4, 5].map(score => <button key={score} type="button" onClick={() => handleScoreChange(category.id, score)} className={`w-10 h-10 rounded-full flex items-center justify-center focus:outline-none ${scores[category.id] === score ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`} data-id="element-1531">
                          {score}
                        </button>)}
                    </div>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-1532">
                      Excellent
                    </span>
                  </div>
                </div>)}
              <div className="pt-4" data-id="element-1533">
                <button type="submit" disabled={!allCategoriesRated} className={`w-full px-4 py-2 rounded-md text-white flex items-center justify-center ${allCategoriesRated ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-1534">
                  <BarChart3Icon className="h-5 w-5 mr-2" data-id="element-1535" />
                  Complete Assessment
                </button>
                {!allCategoriesRated && <p className="text-sm text-red-500 mt-2 text-center" data-id="element-1536">
                    Please rate all categories to complete the assessment
                  </p>}
              </div>
            </div>
          </form>
        </div>}
    </DashboardLayout>;
};
export default MerchantAssessmentPage;